"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { ArrowRight } from "@suite/components/icons";
import { Card } from "@suite/components/ui/card";
import CopyButton from "@suite/components/tools/CopyButton";
import {
  NumberField,
  ResultStat,
  SegmentedToggle,
  SliderField,
} from "@suite/components/tools/scraping-cost-parts";
import { CONTACT_SALES_URL } from "@/lib/constants";
import { buildGbTiers } from "@/lib/pricing-utils";
import { useProductsInfo } from "@/lib/products-info";
import {
  DEFAULT_INPUTS,
  INPUT_LIMITS,
  calculate,
  formatGb,
  formatUsd,
  inputsFromSearchParams,
  inputsToSearchParams,
  type CalculatorInputs,
} from "@/lib/scraping-cost";
import {
  DEFAULT_PROXY_TYPE,
  PROXY_PRODUCTS,
  parseProxyType,
  type ProxyType,
} from "@/lib/scraping-cost-products";

const TOOL_PATH = "/tools/scraping-cost-calculator";

export default function ScrapingCostCalculator() {
  const posthog = usePostHog();
  const info = useProductsInfo();
  const tiersByType = useMemo(
    () => ({
      residential: buildGbTiers(
        info?.[PROXY_PRODUCTS.residential.productsInfoKey] ?? [],
        PROXY_PRODUCTS.residential.fallbackTiers
      ),
      mobile: buildGbTiers(
        info?.[PROXY_PRODUCTS.mobile.productsInfoKey] ?? [],
        PROXY_PRODUCTS.mobile.fallbackTiers
      ),
    }),
    [info]
  );

  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_INPUTS);
  const [proxyType, setProxyType] = useState<ProxyType>(DEFAULT_PROXY_TYPE);
  const [shareUrl, setShareUrl] = useState("");
  const interacted = useRef(false);
  const hydrated = useRef(false);

  // Restore a shared calculation from the URL, then keep the URL in sync as inputs change.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length > 0) {
      setInputs(inputsFromSearchParams(params));
      setProxyType(parseProxyType(params.get("type")));
    }
    hydrated.current = true;
    posthog?.capture("calculator_viewed", { shared_link: params.size > 0 });
  }, [posthog]);

  useEffect(() => {
    if (!hydrated.current) return;
    const params = inputsToSearchParams(inputs);
    if (proxyType !== DEFAULT_PROXY_TYPE) params.set("type", proxyType);
    const query = params.toString();
    const url = `${window.location.origin}${TOOL_PATH}${query ? `?${query}` : ""}`;
    setShareUrl(url);
    window.history.replaceState(null, "", `${TOOL_PATH}${query ? `?${query}` : ""}`);
  }, [inputs, proxyType]);

  const product = PROXY_PRODUCTS[proxyType];
  const otherType: ProxyType = proxyType === "mobile" ? "residential" : "mobile";
  const result = useMemo(
    () => calculate(inputs, tiersByType[proxyType]),
    [inputs, tiersByType, proxyType]
  );
  const otherResult = useMemo(
    () => calculate(inputs, tiersByType[otherType]),
    [inputs, tiersByType, otherType]
  );

  // One event per burst of edits, not one per keystroke.
  useEffect(() => {
    if (!interacted.current) return;
    const timer = setTimeout(() => {
      posthog?.capture("calculation_completed", {
        proxy_type: proxyType,
        usable_pages_per_month: inputs.usablePagesPerMonth,
        browser_share_pct: inputs.browserSharePct,
        success_rate_pct: inputs.successRatePct,
        monthly_gb: Math.round(result.monthlyGb * 100) / 100,
        recommended_plan_gb: result.plan?.tier.gb ?? null,
        cost_per_1k_usable: Math.round(result.costPer1kUsable * 1000) / 1000,
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [inputs, result, proxyType, posthog]);

  const update = useCallback(
    <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => {
      interacted.current = true;
      setInputs((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const changeProxyType = (next: ProxyType) => {
    interacted.current = true;
    setProxyType(next);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:items-start">
      <Card className="gap-6 p-5 sm:p-6">
        <div>
          <h2 className="text-foreground text-lg font-semibold">Your workload</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Every number below is an assumption you control. Nothing leaves your browser.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-foreground text-sm font-medium">Proxy type</span>
            <SegmentedToggle
              label="Proxy type"
              value={proxyType}
              options={[
                { id: "residential", label: PROXY_PRODUCTS.residential.label },
                { id: "mobile", label: PROXY_PRODUCTS.mobile.label },
              ]}
              onChange={changeProxyType}
            />
            <p className="text-muted-foreground text-xs leading-relaxed">
              Priced with our current per-GB plans.
            </p>
          </div>
          <NumberField
            label="Usable pages per month"
            value={inputs.usablePagesPerMonth}
            onChange={(v) => update("usablePagesPerMonth", v ?? DEFAULT_INPUTS.usablePagesPerMonth)}
            min={INPUT_LIMITS.usablePagesPerMonth.min}
            max={INPUT_LIMITS.usablePagesPerMonth.max}
            step={1000}
            hint="Pages that return the data you actually need."
          />
        </div>

        <SliderField
          label="Pages that need a browser (JavaScript rendering)"
          value={inputs.browserSharePct}
          onChange={(v) => update("browserSharePct", v)}
          min={0}
          max={100}
          step={5}
          display={`${inputs.browserSharePct}%`}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <NumberField
            label="Traffic per static page"
            value={inputs.staticKbPerAttempt}
            onChange={(v) => update("staticKbPerAttempt", v ?? DEFAULT_INPUTS.staticKbPerAttempt)}
            min={INPUT_LIMITS.staticKbPerAttempt.min}
            max={INPUT_LIMITS.staticKbPerAttempt.max}
            suffix="KB"
            hint="HTML over plain HTTP. Compressed HTML is often 50–300 KB."
          />
          <NumberField
            label="Traffic per browser-rendered page"
            value={inputs.browserKbPerAttempt}
            onChange={(v) => update("browserKbPerAttempt", v ?? DEFAULT_INPUTS.browserKbPerAttempt)}
            min={INPUT_LIMITS.browserKbPerAttempt.min}
            max={INPUT_LIMITS.browserKbPerAttempt.max}
            suffix="KB"
            hint="Scripts, images and API calls add up. Blocking images and fonts cuts this sharply."
          />
        </div>

        <SliderField
          label="Usable result rate"
          value={inputs.successRatePct}
          onChange={(v) => update("successRatePct", v)}
          min={INPUT_LIMITS.successRatePct.min}
          max={100}
          display={`${inputs.successRatePct}%`}
          hint="Share of attempts that return usable data. Failed attempts still use traffic, so this is the biggest driver of cost. Measure your own."
        />

        <details className="group border-border rounded-lg border">
          <summary className="text-foreground cursor-pointer px-4 py-3 text-sm font-medium">
            Advanced assumptions
          </summary>
          <div className="grid gap-5 px-4 pb-4">
            <SliderField
              label="Failed attempt size"
              value={inputs.failedWeightPct}
              onChange={(v) => update("failedWeightPct", v)}
              min={0}
              max={100}
              step={5}
              display={`${inputs.failedWeightPct}%`}
              hint="Block pages and timeouts are often smaller than real pages. 100% is the conservative default."
            />
          </div>
        </details>
      </Card>

      <div className="flex flex-col gap-4 lg:sticky lg:top-32">
        <Card className="gap-6 p-5 sm:p-6" aria-live="polite">
          <dl className="flex flex-col gap-6">
            <ResultStat
              emphasis
              label="Cost per 1,000 usable pages"
              value={formatUsd(result.costPer1kUsable)}
              detail="Proxy traffic at our plan rate. Excludes compute, engineering and any retries beyond the usable result rate you set."
            />
            <div className="grid grid-cols-2 gap-5">
              <ResultStat
                label="Monthly traffic"
                value={formatGb(result.monthlyGb)}
                detail={`${Math.round(result.trafficPerUsableKb).toLocaleString("en-US")} KB per usable page`}
              />
              <ResultStat
                label="Total attempts"
                value={Math.round(result.totalAttempts).toLocaleString("en-US")}
                detail={`${result.attemptsPerUsable.toFixed(2)} per usable page`}
              />
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed">
              Same workload on {PROXY_PRODUCTS[otherType].label.toLowerCase()} proxies:{" "}
              <span className="text-foreground font-medium tabular-nums">
                {formatUsd(otherResult.costPer1kUsable)}
              </span>{" "}
              per 1,000 usable pages
              {otherResult.plan
                ? ` on the ${otherResult.plan.tier.gb.toLocaleString("en-US")} GB plan.`
                : ", above the largest public plan."}{" "}
              Success rate may differ between proxy types, so adjust it for each.
            </p>
          </dl>

          <div className="border-border border-t pt-5">
            {result.plan ? (
              <>
                <p className="text-muted-foreground text-xs font-medium">Plan you would need</p>
                <p className="text-foreground mt-1 text-2xl font-semibold tabular-nums">
                  {result.plan.tier.gb.toLocaleString("en-US")} GB
                  <span className="text-muted-foreground text-base font-normal">
                    {" "}
                    · {formatUsd(result.plan.tier.totalPrice)}/month
                  </span>
                </p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  Uses about {Math.round(result.plan.utilisation * 100)}% of the plan at $
                  {result.plan.tier.monthlyPPG}/GB. {product.planNote}
                </p>
              </>
            ) : (
              <>
                <p className="text-foreground text-base font-semibold">
                  Above our largest public plan
                </p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  At {formatGb(result.monthlyGb)} a month you are in custom-pricing territory. Cost
                  above uses the largest tier&apos;s rate as a ceiling.
                </p>
              </>
            )}
            <Link
              href={result.plan ? product.pricingHref : CONTACT_SALES_URL}
              onClick={() =>
                posthog?.capture("pricing_clicked", {
                  location: "scraping_cost_calculator",
                  proxy_type: proxyType,
                  recommended_plan_gb: result.plan?.tier.gb ?? null,
                  monthly_gb: Math.round(result.monthlyGb * 100) / 100,
                })
              }
              className="bg-primary text-primary-foreground mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition-opacity hover:opacity-90"
            >
              {result.plan ? product.pricingCta : "Talk to sales"}
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </Card>

        <div className="flex items-center justify-between gap-3 px-1">
          <p className="text-muted-foreground text-xs">Share this calculation</p>
          {shareUrl && (
            <CopyButton
              value={shareUrl}
              label="calculator link"
              className="text-muted-foreground text-xs"
            />
          )}
        </div>
      </div>
    </div>
  );
}
